import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useMemo } from 'react'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export type ViewMode = 'card' | 'table'

export interface UseActiveDocumentPreviewParams {
  documentResults: DocumentTableRow[]
  activeIndex: number
  selectedDocumentId: string | null
  selectedTableDocument: DocumentTableRow | null
  isLoadingSelectedDocument: boolean
  contentFilter: DocumentsViewerContentFilter
  viewMode: ViewMode
}

export interface UseActiveDocumentPreviewResult {
  activeDocument: DocumentTableRow | null
  isPreviewOpen: boolean
  isLoadingPreview: boolean
}

/**
 * Shared hook for active document preview state in both card and table views.
 * Returns the active document (from card list index or table selection) and loading
 * state so a single DocumentPreviewPanel can be used for both.
 */
export function useActiveDocumentPreview({
  documentResults,
  activeIndex,
  selectedDocumentId,
  selectedTableDocument,
  isLoadingSelectedDocument,
  contentFilter,
  viewMode,
}: UseActiveDocumentPreviewParams): UseActiveDocumentPreviewResult {
  return useMemo(() => {
    if (contentFilter !== 'documents') {
      return {
        activeDocument: null,
        isPreviewOpen: false,
        isLoadingPreview: false,
      }
    }

    if (viewMode === 'card') {
      const activeDocument = documentResults[activeIndex] ?? null
      return {
        activeDocument,
        isPreviewOpen: !!activeDocument,
        isLoadingPreview: false,
      }
    }

    // Table view
    const activeDocument = selectedTableDocument
    return {
      activeDocument,
      isPreviewOpen: !!selectedDocumentId,
      isLoadingPreview: !!selectedDocumentId && isLoadingSelectedDocument,
    }
  }, [
    contentFilter,
    viewMode,
    documentResults,
    activeIndex,
    selectedDocumentId,
    selectedTableDocument,
    isLoadingSelectedDocument,
  ])
}
